/**
 * CardEditModal — add/edit a Phase 2-5 stub card inline from the
 * VEROOGLE demo board.  Shared by the "Edit" pencil button and the
 * "Add Card" button at the end of each phase accordion.
 */
import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { apiClient } from "@/lib/auth";
import { useDemoBrand } from "@/lib/demoBrand";
import { X, Save, Trash2 } from "lucide-react";
import { ICON_KEYS, resolveIcon } from "@/components/clientDemo/verooglIcons";

const PHASES = [
  { id: 1, label: "Phase 1 · Lead Intelligence & Acquisition" },
  { id: 2, label: "Phase 2 · Onboarding & Verification" },
  { id: 3, label: "Phase 3 · Portfolio Risk & Dispute Ops" },
  { id: 4, label: "Phase 4 · Accounting & Terminal Logistics" },
  { id: 5, label: "Phase 5 · Automation, Analytics & AI" },
  { id: 6, label: "Phase 6 · Custom Specialty Engines" },
];

export default function CardEditModal({ card, defaultPhase = 2, onClose, onSaved, onDeleted }) {
  const { slug } = useDemoBrand();
  const isEdit = !!card;
  const [draft, setDraft] = useState(() => ({
    phase:    card?.phase ?? defaultPhase,
    card_id:  card?.card_id ?? "",
    title:    card?.title ?? "",
    hint:     card?.hint ?? "",
    icon_key: card?.icon_key ?? "radio",
  }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = async () => {
    if (!draft.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        const r = await apiClient.patch(`/admin/demos/${slug}/cards/${card.id}`, draft);
        saved = r.data;
      } else {
        const r = await apiClient.post(`/admin/demos/${slug}/cards`, draft);
        saved = r.data;
      }
      toast.success(isEdit ? "Card updated" : "Card added");
      onSaved?.(saved);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!card) return;
    if (!window.confirm(`Delete "${card.title}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await apiClient.delete(`/admin/demos/${slug}/cards/${card.id}`);
      toast.success("Card deleted");
      onDeleted?.(card.id);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  const Preview = resolveIcon(draft.icon_key);

  return (
    <div
      data-testid="veroogle-card-edit-modal"
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#0C1322] shadow-2xl overflow-hidden">
        <header className="flex items-center justify-between px-5 py-4 border-b border-white/8 bg-gradient-to-r from-brand-cyan/5 via-transparent to-brand-emerald/5">
          <div>
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-brand-cyan">
              {isEdit ? "// EDIT CARD" : "// ADD CARD"}
            </div>
            <h3 className="font-semibold text-white text-lg">
              {isEdit ? card.card_id : "New capability tile"}
            </h3>
          </div>
          <button
            onClick={onClose}
            data-testid="veroogle-card-edit-close"
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">Phase</span>
              <select
                value={draft.phase}
                onChange={(e) => setDraft({ ...draft, phase: Number(e.target.value) })}
                data-testid="veroogle-card-edit-phase"
                className="rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none px-3 py-2 text-sm text-white"
              >
                {PHASES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">Card ID</span>
              <input
                value={draft.card_id}
                onChange={(e) => setDraft({ ...draft, card_id: e.target.value })}
                placeholder="T####cr"
                data-testid="veroogle-card-edit-cardid"
                className="rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none px-3 py-2 text-sm text-white font-mono"
              />
            </label>
          </div>

          <label className="flex flex-col gap-1">
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">Title</span>
            <input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              data-testid="veroogle-card-edit-title"
              className="rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none px-3 py-2 text-sm text-white"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">Short description</span>
            <textarea
              value={draft.hint}
              onChange={(e) => setDraft({ ...draft, hint: e.target.value })}
              rows={2}
              data-testid="veroogle-card-edit-hint"
              className="rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none px-3 py-2 text-sm text-slate-300 resize-none"
            />
          </label>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">Icon</span>
              <div className="flex items-center gap-2 text-slate-400 text-xs">
                Preview: <Preview className="w-4 h-4 text-brand-cyan" />
              </div>
            </div>
            <div className="grid grid-cols-8 gap-1.5 p-2 rounded-lg bg-navy-950 border border-white/10 max-h-40 overflow-y-auto" data-testid="veroogle-card-edit-icons">
              {ICON_KEYS.map((k) => {
                const I = resolveIcon(k);
                const active = k === draft.icon_key;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setDraft({ ...draft, icon_key: k })}
                    data-testid={`veroogle-card-edit-icon-${k}`}
                    title={k}
                    className={`p-2 rounded-md border transition ${
                      active
                        ? "border-brand-cyan bg-brand-cyan/15 text-brand-cyan"
                        : "border-white/5 text-slate-400 hover:text-white hover:border-white/20"
                    }`}
                  >
                    <I className="w-4 h-4 mx-auto" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <footer className="px-5 py-4 border-t border-white/8 bg-white/[0.02] flex items-center justify-between gap-2">
          {isEdit ? (
            <button
              onClick={remove}
              disabled={saving}
              data-testid="veroogle-card-edit-delete"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono tracking-[0.14em] uppercase text-rose-400 hover:text-rose-300 border border-rose-500/40 hover:border-rose-500/70 transition disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          ) : <div />}
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg text-xs font-mono tracking-[0.14em] uppercase text-slate-300 hover:text-white border border-white/10 hover:border-white/30 transition"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="veroogle-card-edit-save"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono tracking-[0.14em] uppercase bg-brand-cyan/90 hover:bg-brand-cyan text-navy-950 font-semibold transition disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" /> {isEdit ? "Save" : "Add card"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
